import { Component, h, Prop, Event, EventEmitter } from '@stencil/core';
import { htmlSanitizer } from '../../../utils/html-sanitizer';

// Define ParsedCtaItem locally
export interface ParsedCtaItem {
  ctaButtonColor: 'primary' | 'secondary' | 'white' | 'link';
  ctaLink: string;
  ctaTitle: string;
  ctaLinkType: 'internal' | 'video' | string;
  ctaLinkTarget: '_self' | 'modal' | string;
  ctaStyle: 'button' | 'link' | string;
}

@Component({
  tag: 'vsr-card',
  styleUrl: 'vsr-card.scss',
  shadow: false,
})
export class VsCard {
  @Prop() type: string;
  @Prop() imageUrl: string;
  @Prop() imageAltText: string;
  @Prop() borderRadius: string;
  @Prop() animation: string;
  @Prop() imageVariant: string;
  @Prop() cardVariant: string;
  @Prop() imageAspectRatio: string = '3:4';
  @Prop() title: string;
  @Prop() description: string;
  @Prop() cardLink: string;
  @Prop() cardLinkType: string;
  @Prop() cardLinkTarget: string = 'same';
  @Prop() buttonText1: string;
  @Prop() buttonTextLink1: string;
  @Prop() buttonText2: string;
  @Prop() theme: 'light' | 'dark' = 'light';
  @Prop() cardCta?: ParsedCtaItem[];

  //video props
  @Prop() mediaType: 'image' | 'single' | 'loop' = 'image';
  @Prop() autoplay: boolean = true;
  @Prop() muted: boolean = true;
  @Prop() videoAlt: string;
  @Prop() mediaSrc: string = '';
  @Prop() poster: string = '';

  @Event() compareClick: EventEmitter<void>;

  private getVideoType(): 'video-single' | 'video-loop' {
    if (this.mediaType != 'image') {
      if (this.mediaType === 'single') {
        return 'video-single';
      } else if (this.mediaType === 'loop') {
        return 'video-loop';
      }
      return this.mediaType;
    }
  }

  private renderCompareIcon() {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <g>
          <path
            d="M10.5 5.5L13 8L10.5 10.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M5.5 10.5L3 8L5.5 5.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </g>
      </svg>
    );
  }

  private renderIconCard() {
    return (
      <vsr-icon-card
        theme={this.theme}
        title={this.title}
        title-href={this.cardLink}
        title-target={this.cardLinkTarget === 'new' ? '_blank' : '_self'}
        description={this.description}
        button-text={this.buttonText1}
        button-href={this.buttonTextLink1}
        button-target="_self"
        img-src={this.imageUrl}
        img-alt={this.imageAltText}
        image-aspect-ratio={this.imageAspectRatio}
      ></vsr-icon-card>
    );
  }

  private renderProductCard() {
    return (
      <vsr-product-card
        theme={this.theme}
        image-url={this.imageUrl}
        image-alt-text={this.imageAltText}
        image-aspect-ratio={this.imageAspectRatio}
        type={this.type}
        title={this.title}
        description={this.description}
        cardCta={this.cardCta}
        button-text-2={this.buttonText2}
        card-link={this.cardLink}
        card-link-target={this.cardLinkTarget}
        onCompareClick={this.compareClick}
      ></vsr-product-card>
    );
  }

  private renderCard() {
    const cardThemeClass = `vsr-card-theme-${this.theme}`;
    const hasLinkClass = this.cardLink ? 'has-card-link' : '';
    const aspectRatioClass = `aspect-ratio-${this.imageAspectRatio.replace(':', '-')}`;
    const is16by9 = this.imageAspectRatio === '16:9' ? 'no-hover-16-9' : '';

    return (
      <div class={`vsr-card ${cardThemeClass} ${hasLinkClass} ${is16by9}`} data-animation-container>
        {this.cardLink ? (
          <a
            href={this.cardLink}
            target={this.cardLinkTarget === 'new' ? '_blank' : '_self'}
            class="vsr-card__image-container-link"
            tabindex="-1"
            alt-text={this.imageAltText || this.title}
          >
            <div
              class={`vsr-card__image-container ${this.imageVariant} ${aspectRatioClass} ${this.borderRadius === 'yes' ? 'b-radius' : ''}`}
            >
              {this.mediaType == 'image' ? (
                <img
                  class={`vsr-card__image ${this.animation === 'pulsate' ? 'pulsate' : 'zoom'} cardReveal ${this.imageAspectRatio === '16:9' ? '' : 'has-hover-effect'}`}
                  src={this.imageUrl}
                  alt={this.imageAltText || this.title}
                />
              ) : this.mediaSrc ? (
                <vsr-video
                  src={this.mediaSrc}
                  poster={this.poster}
                  video-type={this.getVideoType()}
                  autoplay={true}
                  muted={true}
                  video-alt={this.videoAlt}
                  class={`vsr-card__image ${this.animation === 'pulsate' ? 'pulsate' : 'zoom'} cardReveal ${this.imageAspectRatio === '16:9' ? '' : 'has-hover-effect'}`}
                ></vsr-video>
              ) : (
                <img
                  class={`vsr-card__image ${this.animation === 'pulsate' ? 'pulsate' : 'zoom'} cardReveal ${this.imageAspectRatio === '16:9' ? '' : 'has-hover-effect'}`}
                  src={this.imageUrl}
                  alt={this.imageAltText || this.title}
                />
              )}
            </div>
          </a>
        ) : (
          <div
            class={`vsr-card__image-container ${this.imageVariant} ${aspectRatioClass} ${this.borderRadius === 'yes' ? 'b-radius' : ''}`}
          >
            {this.mediaType == 'image' ? (
              <img
                class={`vsr-card__image ${this.animation === 'pulsate' ? 'pulsate' : 'zoom'} cardReveal ${this.imageAspectRatio === '16:9' ? '' : 'has-hover-effect'}`}
                src={this.imageUrl}
                alt={this.imageAltText || this.title}
              />
            ) : (
              <vsr-video
                src={this.mediaSrc}
                poster={this.poster}
                video-type={this.getVideoType()}
                autoplay={true}
                muted={true}
                video-alt={this.videoAlt}
                class={`vsr-card__image ${this.animation === 'pulsate' ? 'pulsate' : 'zoom'} cardReveal ${this.imageAspectRatio === '16:9' ? '' : 'has-hover-effect'}`}
              ></vsr-video>
            )}
          </div>
        )}
        <div class="vsr-card__content blockReveal">
          {this.type && <h3 class="vsr-card__label">{this.type}</h3>}
          {this.cardLink ? (
            <h3 class="vsr-card__title">
              <a
                href={this.cardLink}
                target={this.cardLinkTarget === 'new' ? '_blank' : '_self'}
                tabindex="0"
                innerHTML={htmlSanitizer(this.title)}
              />
            </h3>
          ) : (
            <h3 class="vsr-card__title" tabindex="0" innerHTML={htmlSanitizer(this.title)} />
          )}
          <p class="vsr-card__description" innerHTML={htmlSanitizer(this.description)} />
          {this.cardCta && this.cardCta.length > 0 && (
            <div class="vsr-title-button-group">
              {this.cardCta.map((btn, index) => {
                const isButton = btn.ctaStyle === 'button';
                const target = btn.ctaLinkTarget === 'new' ? '_blank' : '_self';
                const ComponentTag = isButton ? 'vsr-button-v2' : 'vsr-link-button-v2';
                return (
                  <ComponentTag
                    key={index}
                    href={btn.ctaLink || '#'}
                    variant={btn.ctaButtonColor || 'primary'}
                    themeOverride={this.theme}
                    target={target}
                    aria-label={btn.ctaTitle}
                    ctaLinkType={btn.ctaLinkType}
                  >
                    <span innerHTML={htmlSanitizer(btn.ctaTitle)}></span>
                  </ComponentTag>
                );
              })}
            </div>
          )}
          {this.buttonText2 && (
            <div class="vsr-card__actions-row">
              <a href="#" onClick={() => this.compareClick} class="vsr-card__action-button compare">
                {this.buttonText2} {this.renderCompareIcon()}
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }
  // 2up card variant is now handled by vs-content-card-grid component

  render() {
    return [
      this.cardVariant === 'default' && this.renderCard(),
      this.cardVariant === 'product' && this.renderProductCard(),
      this.cardVariant === 'icon' && this.renderIconCard(),
      // 2up card variant is now handled by vs-content-card-grid component
    ];
  }
}
